'use client'

import { useEffect } from 'react'
import Link from 'next/link'
import { Home, Phone } from 'lucide-react'

import { useSiteSettings } from '@/lib/site-settings-context'
import { trackErrorPageView } from '@/lib/track-error'
import { formatPhone } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { HalloweenTrinket } from '@/components/halloween'
import { NeonLogo } from '@/components/neon-logo'
import { PunkHighlight } from '@/components/punk-highlight'
import { TopoFlow } from '@/components/topo-flow'

export default function NotFound() {
  const { phoneNumber } = useSiteSettings()

  useEffect(() => {
    trackErrorPageView({ kind: 'not_found', description: '404 (in-site notFound)' })
  }, [])

  return (
    <main className="texture-stone-dark grain-dark stage-light-dark relative min-h-dvh overflow-hidden bg-[#1a0e20]">
      <TopoFlow
        seed={11}
        lineCount={14}
        scale={0.002}
        speed={0.15}
        strokeColour="rgba(149,248,98,0.08)"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#5b1b5a]/40 via-[#1a0e20]/80 to-[#1a0e20]" />
      <div className="relative z-10 mx-auto flex min-h-dvh max-w-3xl flex-col items-center justify-center px-4 pt-16 pb-32 text-center sm:px-6 sm:pb-16">
        <Link href="/" className="mb-8" aria-label="Rock'n Earthworks home">
          <NeonLogo variant="left-stacked" className="h-24 w-auto" />
        </Link>

        <p className="font-marker text-brand-chartreuse-vivid mb-3 text-2xl sm:text-3xl">
          Took a wrong turn?
        </p>
        <div className="mb-2 flex items-end justify-center gap-2" aria-hidden="true">
          <HalloweenTrinket character="ghost" className="size-20" />
          <HalloweenTrinket pool="dig-site" initial={5} className="size-14" />
        </div>
        <h1 className="font-display text-5xl font-bold text-white sm:text-6xl lg:text-7xl">
          404: <PunkHighlight rotation="-1deg">Off the Map</PunkHighlight>
        </h1>
        <p className="mt-6 max-w-lg text-lg text-white/60">
          That page doesn&apos;t exist (or it&apos;s moved). Head home or give us a bell if you were
          after something specific.
        </p>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:gap-4">
          <Button
            asChild
            size="lg"
            className="bg-brand-purple hover:bg-brand-purple-dark h-12 px-6 text-xl tracking-wide text-white"
          >
            <Link href="/">
              <Home className="mr-2 size-5" aria-hidden="true" />
              Back home
            </Link>
          </Button>
          {phoneNumber && (
            <Button
              asChild
              variant="ghost"
              size="lg"
              className="border-brand-chartreuse/40 bg-brand-chartreuse/10 text-brand-chartreuse-vivid hover:border-brand-chartreuse hover:bg-brand-chartreuse/25 h-12 border px-6 text-lg font-bold transition-all hover:text-white"
            >
              <a href={`tel:${phoneNumber}`}>
                <Phone className="mr-2 size-5" aria-hidden="true" />
                {formatPhone(phoneNumber)}
              </a>
            </Button>
          )}
        </div>
      </div>
    </main>
  )
}
